import type { JSX } from 'react';
import type { AvailableUnitGroup } from '@/components/public/units';
import type { PublishedUnitSummary } from '@/lib/types/publicProperty';
import type {
  AvailabilitiesMapPanelContent,
  AvailabilitiesMapUnitRowContent,
  AvailabilitiesQuickViewContent,
} from '@/lib/types/siteSettings';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import { AvailabilitiesMapPanelAddressRow } from './AvailabilitiesMapPanelAddressRow';
import { AvailabilitiesMapPanelUnitRow } from './AvailabilitiesMapPanelUnitRow';
import { AvailabilitiesUnitQuickView } from './AvailabilitiesUnitQuickView';
import type { AvailabilitiesAddressEntry } from './availabilitiesMapListTypes';
import { applyTemplate } from '@/lib/utils/textTemplates';

interface AvailabilitiesMapPanelProps {
  id?: string;
  addressEntries: AvailabilitiesAddressEntry[];
  selectedGroup: AvailableUnitGroup | null;
  selectedUnit: PublishedUnitSummary | null;
  selectedPropertyId: string | null;
  selectedUnitId: string | null;
  content: AvailabilitiesMapPanelContent;
  mapUnitRowContent: AvailabilitiesMapUnitRowContent;
  quickViewContent: AvailabilitiesQuickViewContent;
  availabilityLabels: { immediateLabel: string; negotiableLabel: string };
  onBack: () => void;
  onSelectProperty: (propertyId: string) => void;
  onSelectUnit: (unitId: string) => void;
}

export function AvailabilitiesMapPanel({
  id,
  addressEntries,
  selectedGroup,
  selectedUnit,
  selectedPropertyId,
  selectedUnitId,
  content,
  mapUnitRowContent,
  quickViewContent,
  availabilityLabels,
  onBack,
  onSelectProperty,
  onSelectUnit,
}: AvailabilitiesMapPanelProps): JSX.Element {
  const selectedAddressLabel = selectedGroup
    ? formatPropertyAddress(selectedGroup.property.address)
    : null;
  const selectedUnitCountLabel = selectedGroup
    ? formatSuiteCountLabel(selectedGroup.units.length, content)
    : null;

  return (
    <aside
      id={id}
      className="flex min-h-[360px] flex-col overflow-hidden rounded-3xl border border-surface-subtle bg-white shadow-card lg:h-[min(72vh,760px)]"
    >
      <div className="flex-1 overflow-y-auto p-5">
        {selectedGroup ? (
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex min-h-11 items-center justify-center self-start rounded-full border border-brand-primary/20 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wide text-brand-primary transition hover:border-brand-primary/40 hover:bg-brand-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
            >
              {content.backLabel}
            </button>

            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
                {content.addressEyebrow}
              </p>
              <h3 className="text-lg font-semibold text-brand-primary">{selectedAddressLabel}</h3>
              <p className="text-sm text-text-primary/70">{selectedGroup.property.submarket}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
                {selectedUnitCountLabel}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {content.addressesEyebrow}
            </p>
            <h3 className="text-lg font-semibold text-brand-primary">{content.browseTitle}</h3>
            <p className="text-sm text-text-primary/70">{content.browseDescription}</p>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2">
          {selectedGroup
            ? selectedGroup.units.map((unit) => (
                <AvailabilitiesMapPanelUnitRow
                  key={unit.id}
                  unit={unit}
                  selected={selectedUnitId === unit.id}
                  content={mapUnitRowContent}
                  availabilityLabels={availabilityLabels}
                  onSelect={() => {
                    onSelectUnit(unit.id);
                  }}
                />
              ))
            : addressEntries.map((entry) => (
                <AvailabilitiesMapPanelAddressRow
                  key={entry.group.property.id}
                  entry={entry}
                  selected={selectedPropertyId === entry.group.property.id}
                  content={content}
                  onSelect={() => {
                    onSelectProperty(entry.group.property.id);
                  }}
                />
              ))}
        </div>
      </div>

      <div className="border-t border-brand-primary/10 bg-surface-subtle p-5 shadow-[0_-12px_24px_rgba(10,33,64,0.08)]">
        {selectedGroup && selectedUnit ? (
          <AvailabilitiesUnitQuickView
            property={selectedGroup.property}
            unit={selectedUnit}
            content={quickViewContent}
            availabilityLabels={availabilityLabels}
          />
        ) : selectedGroup ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {content.selectUnitEyebrow}
            </p>
            <p className="text-sm text-text-primary/70">{content.selectUnitDescription}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-accent">
              {content.tapPinEyebrow}
            </p>
            <p className="text-sm text-text-primary/70">{content.tapPinDescription}</p>
          </div>
        )}
      </div>
    </aside>
  );
}

function formatSuiteCountLabel(count: number, content: AvailabilitiesMapPanelContent): string {
  return count === 1
    ? content.suiteCountSingular
    : applyTemplate(content.suiteCountPlural, { count: count.toString() });
}
